import { Add } from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { theme } from "../../../Theme/theme";
import { AppContext } from "../../../Utilities/AppContext";
import { GET, PATCH, POST } from "../../../Utilities/BaseService";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";

type DateConstraintVariant =
    | "collection-back"
    | "collection-forward"
    | "reading-back"
    | "reading-forward"
    | "connection-back"
    | "connection-forward";

type DateConstraint = { key: DateConstraintVariant; value: string };

const addMeter = (meter: any) => POST("/customer/meter", meter);
const updateMeter = (meter: any, id: number) =>
    PATCH("/customer/meter", meter, { id });

const AddMeter = ({ updateMode }: any) => {
    const [id, setId] = useState("");
    const [meter, setMeter] = useState<any>({
        meterNo: "",
        connectionCharges: 0,
        connectionDate: "",
        property: "",
        connectionType: "",
        powerstation: "",
        district: "",
        village: "",
        priceType: "",
        hamlet: "",
        status: "active",
        lineRentEditable: false,
    });

    const [feedback, setFeedback] = useState<string>();
    const [details, setDetails] = useState<any>({ property: { id: "" } });
    const [clearAll, setClearAll] = useState({
        district: false,
        village: false,
        hamlet: false,
        powerstation: false,
    });
    const [defaultRegions, setDefaultRegions] = useState<any>({
        district: "",
        village: "",
        hamlet: "",
        powerstation: "",
        property: "",
        connectionType: "",
    });

    const { user } = useContext(AppContext);
    const location = useLocation();

    // Fetch meter by ID
    const getMeterById = () => GET(`/customer/meter?id=${id}`);

    const { data: meterById } = useQuery(["Meter", id], getMeterById, {
        enabled: Boolean(id),
        refetchOnMount: true,
        onSuccess(response) {
            if (response.data.rows.length > 0) {
                const _m = response.data.rows[0];
                console.log(_m, "MeterInfo");

                const newDefaults = {
                    district: _m.district?.id || "",
                    village: _m.village?.id || "",
                    hamlet: _m.hamlet?.id || "",
                    powerstation: _m.powerstation?.id || "",
                    property: _m.property?.id || "",
                    connectionType: _m.connectionType?.id || "",
                };

                setDefaultRegions(newDefaults);

                setMeter({
                    meterNo: _m.meterNo || "",
                    connectionCharges: _m.connectionCharges || 0,
                    connectionDate: _m.connectionDate || "",
                    priceType: _m.priceType?.id || "",
                    lineRentEditable: _m.lineRentEditable || false,
                    status: _m.status || "active",
                    ...newDefaults,
                });
            }
        },
    });

    const handleChange = (ev: any) => {
        const { name, value } = ev.target;
        setMeter({ ...meter, [name]: value });
    };

    const addMutation = useMutation(addMeter, {
        onSuccess: (res) => setFeedback(res.data.message),
        onError: (err: any) => setFeedback(err.response.data.message),
    });

    const updateMutation = useMutation(
        () =>
            updateMeter(
                {
                    ...Object.fromEntries(
                        Object.entries(meter).filter(
                            ([_, v]) => v && v !== "all"
                        )
                    ),
                    lineRentEditable: meter.lineRentEditable,
                    connectionCharges: meter.connectionCharges ?? 0,
                },
                id as any
            ),
        {
            onSuccess: (res) => setFeedback(res.data.message),
            onError: (err: any) => setFeedback(err.response.data.message),
        }
    );

    const handleSubmit = (ev: any) => {
        ev.preventDefault();
        if (!updateMode) {
            console.log(meter.connectionCharges,"connectionCharges");
            addMutation.mutate({
                ...Object.fromEntries(
                    Object.entries(meter).filter(([_, v]) => v && v !== "all")
                ),
                lineRentEditable: meter.lineRentEditable,
                connectionCharges: meter.connectionCharges ? meter.connectionCharges : 0,
                district: meter.property?.district?.id as any,
                hamlet: meter?.property?.hamlet?.id,
                village: meter?.property?.village?.id,
                property: meter?.property?.id,
            });
        } else {
            updateMutation.mutate();
        }
    };

    // Restrict connection date range
    function manipulateDate(
        currentDate: Date,
        days: number,
        op: "add" | "sub"
    ) {
        const d = new Date(currentDate);
        const ms = 24 * 60 * 60 * 1000;
        return new Date(d.getTime() + (op === "add" ? days : -days) * ms);
    }

    function getRestrictedDate(target: DateConstraintVariant) {
        const constraint = user.constraints.find(
            (c: DateConstraint) => c.key === target
        );
        if (constraint) {
            const days = Number(constraint.value);
            if (target === "connection-back")
                return manipulateDate(new Date(), days, "sub")
                    .toISOString()
                    .slice(0, 10);
            if (target === "connection-forward")
                return manipulateDate(new Date(), days, "add")
                    .toISOString()
                    .slice(0, 10);
        }
        return undefined;
    }

    useEffect(() => {
        if (updateMode) {
            const params = new URLSearchParams(location.search);
            const _id = params.get("id");
            if (_id) setId(_id);
        }
    }, [updateMode]);

    useEffect(() => {
        if (meter.district === "all")
            setClearAll({
                district: true,
                village: true,
                hamlet: true,
                powerstation: true,
            });
        else if (meter.village === "all")
            setClearAll({ ...clearAll, village: true, hamlet: true });
        else if (meter.hamlet === "all")
            setClearAll({ ...clearAll, hamlet: true });

        return () =>
            setClearAll({
                district: false,
                village: false,
                hamlet: false,
                powerstation: false,
            });
    }, [meter.district, meter.village, meter.hamlet]);

    useEffect(() => {
        setMeter({
            ...meter,
            property: details.property?.id ?? "",
            district: details.property?.district?.id ?? "",
            village: details.property?.village?.id ?? "",
            hamlet: details.property?.hamlet?.id ?? "",
        });
    }, [details.property]);

    return (
        <>
            <Card
                elevation={0}
                sx={{ bgcolor: theme.palette.common.white, mb: "1rem" }}
            >
                <CardHeader
                    title={updateMode ? "Update Meter" : "Add Meter"}
                    titleTypographyProps={{ color: "gray" }}
                    sx={{ textTransform: "capitalize" }}
                />

                <CardContent>
                    <Divider sx={{ mb: 5 }} />
                    <Grid
                        container
                        component="form"
                        onSubmit={handleSubmit}
                        spacing={1}
                    >
                        <Grid item xs={12} md={6} lg={4}>
                            <TextField
                                size="small"
                                name="meterNo"
                                value={meter.meterNo}
                                label="Meter No."
                                onChange={handleChange}
                                fullWidth
                                required
                                disabled={updateMode}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <TextField
                                label="Connection Charges"
                                size="small"
                                type="number"
                                value={meter.connectionCharges}
                                onChange={(e) =>
                                    setMeter({
                                        ...meter,
                                        connectionCharges: 
                                            e.target.value
                                        ? parseInt(e.target.value)
                                        : 0,
                                    })
                                }
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <TextField
                                size="small"
                                name="connectionDate"
                                value={meter.connectionDate}
                                label="Connection Date"
                                onChange={handleChange}
                                fullWidth
                                type="date"
                                required
                                InputLabelProps={{ shrink: true }}
                                disabled={updateMode}
                                inputProps={{
                                    min: getRestrictedDate("connection-back"),
                                    max: getRestrictedDate(
                                        "connection-forward"
                                    ),
                                }}
                            />
                        </Grid>

                        {/* ✅ Property */}
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Property"
                                _name="property"
                                api="/customer/property"
                                filter={meter}
                                setFilter={setMeter}
                                disabled={updateMode}
                                defaultValue={
                                    (updateMode && defaultRegions.property) ||
                                    ""
                                }
                                key={defaultRegions.property || "property-new"}
                                getFullRecord
                            />
                        </Grid>

                        {/* ✅ Connection Type */}
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Connection Type"
                                _name="connectionType"
                                api="/connection"
                                filter={meter}
                                setFilter={setMeter}
                                defaultValue={
                                    defaultRegions.connectionType || ""
                                }
                                key={
                                    defaultRegions.connectionType ||
                                    "connectionType-new"
                                }
                            />
                        </Grid>

                        {/* Price Type */}
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Price Type"
                                _name="priceType"
                                api="/priceType"
                                filter={meter}
                                setFilter={setMeter}
                                required
                                defaultValue={
                                    meterById?.data?.rows?.[0]?.priceType?.id ||
                                    ""
                                }
                                key={
                                    meterById?.data?.rows?.[0]?.priceType?.id ||
                                    "new"
                                }
                            />
                        </Grid>

                        {updateMode && (
                            <>
                                <Grid item xs={12} md={6} lg={4}>
                                    <UpdatedSearchableInput
                                        api="/region/district"
                                        filter={meter}
                                        setFilter={setMeter}
                                        label="District"
                                        _name="district"
                                        defaultValue={
                                            defaultRegions.district || ""
                                        }
                                        key={
                                            defaultRegions.district ||
                                            "district-new"
                                        }
                                        clearAll={clearAll.district}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} lg={4}>
                                    <UpdatedSearchableInput
                                        api="/region/village"
                                        filter={meter}
                                        setFilter={setMeter}
                                        label="Village"
                                        _name="village"
                                        defaultValue={
                                            defaultRegions.village || ""
                                        }
                                        key={
                                            defaultRegions.village ||
                                            "village-new"
                                        }
                                        dep={meter.district}
                                        params={{ district: meter.district }}
                                        clearAll={clearAll.village}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} lg={4}>
                                    <UpdatedSearchableInput
                                        api="/region/hamlet"
                                        filter={meter}
                                        setFilter={setMeter}
                                        label="Hamlet"
                                        _name="hamlet"
                                        defaultValue={
                                            defaultRegions.hamlet || ""
                                        }
                                        key={
                                            defaultRegions.hamlet ||
                                            "hamlet-new"
                                        }
                                        dep={meter.village}
                                        params={
                                            meter.village &&
                                            meter.village !== "all"
                                                ? { village: [meter.village] }
                                                : { village: [0] }
                                        }
                                        clearAll={clearAll.hamlet}
                                        required
                                    />
                                </Grid>

                                <Grid item xs={12} md={6} lg={4}>
                                    <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        value={meter.status}
                                        label="Status"
                                        onChange={(e) =>
                                            setMeter({
                                                ...meter,
                                                status: e.target.value,
                                            })
                                        }
                                    >
                                        <MenuItem value="active">
                                            Active
                                        </MenuItem>
                                        <MenuItem value="inactive">
                                            Inactive
                                        </MenuItem>
                                    </TextField>
                                </Grid>
                            </>
                        )}

                        {/* Powerstation */}
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                _name="powerstation"
                                api="/powerstation"
                                filter={meter}
                                setFilter={setMeter}
                                defaultValue={defaultRegions.powerstation || ""}
                                key={
                                    defaultRegions.powerstation ||
                                    "powerstation-new"
                                }
                                clearAll={clearAll.powerstation}
                                required
                                dep={meter.district}
                                params={{
                                    district: meter.district ?? undefined,
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={4}>
                            <FormControlLabel
                                label="Is line rent editable?"
                                control={
                                    <Checkbox
                                        checked={meter.lineRentEditable}
                                        onChange={(e) =>
                                            setMeter({
                                                ...meter,
                                                lineRentEditable:
                                                    e.target.checked,
                                            })
                                        }
                                    />
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                startIcon={<Add />}
                                endIcon={
                                    addMutation.isLoading ||
                                    updateMutation.isLoading ? (
                                        <CircularProgress
                                            color="secondary"
                                            size="1rem"
                                        />
                                    ) : null
                                }
                                color="secondary"
                                variant="outlined"
                                size="small"
                                type="submit"
                            >
                                {updateMode ? "Update" : "Add"} Meter
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {(addMutation.isSuccess || updateMutation.isSuccess) && (
                <Alert severity="success">{feedback}.</Alert>
            )}
            {(addMutation.isError || updateMutation.isError) && (
                <Alert severity="error">{feedback}.</Alert>
            )}
        </>
    );
};

export default AddMeter;
